import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Loader2, CheckCircle2, AlertCircle, Sparkles, Lock, Workflow, User, Copy, Check } from 'lucide-react';
import { expenseApi, formatApiError } from '../services/api.ts';

// === Approval Calculation Logic (ported from Excel formula) ===
const SJ_COMPANY = 'SJ';
const GROUP1_COMPANIES = new Set(['AS','AXL','CC01','EE','ESP','KF','NFM','OME','OS','PS','PSC','RCS','RF1','SMT','VM2']);
const GROUP2_COMPANIES = new Set(['AIW','CPAA01','EHJ1','EHJ10','EHJ11','EHJ12','EHJ14','EHJ15','EHJ16','EHJ16D','EHJ16L','EHJ17','EHJ2','EHJ3','EHJ4','EHJ5','EHJ6','EHJ7','EHJ8','EHJ9','EHJM','HK KXC','MY EHJ1','MY WXF','NP','PT','SJ88','SW','TS','VM']);

// 5M approvers by company
const APPROVER_5M: Record<string, string> = {
  'AIW':'JOHN.H','AS':'KEITH.P','AXL':'TOM.L','CC01':'RAYMOND.C','CPAA01':'RAYMOND.C',
  'EE':'KYLIN.L','EHJ1':'KYLIN.L','EHJ10':'KYLIN.L','EHJ11':'KYLIN.L','EHJ12':'KYLIN.L',
  'EHJ14':'KYLIN.L','EHJ15':'KYLIN.L','EHJ16':'KYLIN.L','EHJ16D':'KYLIN.L','EHJ16L':'KYLIN.L',
  'EHJ17':'KYLIN.L','EHJ2':'KYLIN.L','EHJ3':'KYLIN.L','EHJ4':'KYLIN.L','EHJ5':'KYLIN.L',
  'EHJ6':'KYLIN.L','EHJ7':'KYLIN.L','EHJ8':'KYLIN.L','EHJ9':'KYLIN.L','EHJM':'KYLIN.L',
  'ESP':'BERTON.L','HK KXC':'CAMILA.W','KF':'JACKSON.H','MY EHJ1':'KYLIN.L','MY WXF':'CAMILA.W',
  'NFM':'','NP':'ELLIE.Z','OME':'JESSICA.F','OS':'JACKSON.H','PS':'MILIAN.Q',
  'PSC':'JASON.T','PT':'MILIAN.Q','RCS':'CARRY.Y','RF1':'JACKSON.H','SJ':'ROXY.W',
  'SJ88':'ASHIN.H','SMT':'MILIAN.Q','SW':'JOHNSON.Z','TS':'KEN.C','VM':'JOHNSON.Z','VM2':'JOHNSON.Z',
};

// 6M approvers by company
const APPROVER_6M: Record<string, string> = {
  'AIW':'LILLIAN.LIN','AS':'Yao.Y','AXL':'EDDY.M','CC01':'Vincent','CPAA01':'Vincent',
  'EE':'SHIRLEY.W','EHJ1':'SHIRLEY.W','EHJ10':'SHIRLEY.W','EHJ11':'SHIRLEY.W','EHJ12':'SHIRLEY.W',
  'EHJ14':'SHIRLEY.W','EHJ15':'SHIRLEY.W','EHJ16':'SHIRLEY.W','EHJ16D':'SHIRLEY.W','EHJ16L':'SHIRLEY.W',
  'EHJ17':'SHIRLEY.W','EHJ2':'SHIRLEY.W','EHJ3':'SHIRLEY.W','EHJ4':'SHIRLEY.W','EHJ5':'SHIRLEY.W',
  'EHJ6':'SHIRLEY.W','EHJ7':'SHIRLEY.W','EHJ8':'SHIRLEY.W','EHJ9':'SHIRLEY.W','EHJM':'SHIRLEY.W',
  'ESP':'EDDY.M','HK KXC':'LILLIAN.LIN','KF':'SHIRLEY.W','MY EHJ1':'SHIRLEY.W','MY WXF':'LILLIAN.LIN',
  'NFM':'','NP':'LILLIAN.LIN','OME':'KENNETH.L','OS':'SHIRLEY.W','PS':'Vincent',
  'PSC':'SHIRLEY.W','PT':'Vincent','RCS':'EDDY.M','RF1':'SHIRLEY.W','SJ':'JEFF.L',
  'SJ88':'VINCENT.C','SMT':'VINCENT.C','SW':'SHIRLEY.W','TS':'KENNETH.L','VM':'SHIRLEY.W','VM2':'SHIRLEY.W',
};

const calcApprovalRole = (company: string, amount: number): string => {
  if (!company) return '';
  const c = company.trim();
  if (c === SJ_COMPANY) {
    if (amount < 4000) return '5M';
    if (amount < 8000) return '6M';
    return 'SCOTT';
  }
  if (GROUP1_COMPANIES.has(c)) {
    if (amount < 1000) return '5M';
    if (amount < 2000) return '6M';
    return 'SCOTT';
  }
  if (GROUP2_COMPANIES.has(c)) {
    if (amount < 2000) return '5M';
    if (amount < 5000) return '6M';
    return 'SCOTT';
  }
  return '公司未定義';
};

const calcApprover = (role: string, company: string): string => {
  if (!role || !company) return '';
  if (role === 'SCOTT') return 'SCOTT';
  const c = company.trim();
  if (role === '5M') return APPROVER_5M[c] || '';
  if (role === '6M') return APPROVER_6M[c] || '';
  return '';
};

const generateApprovalTextHelper = (data: {
  application_date: string;
  expense_company: string;
  expense_purpose: string;
  warehouse: string;
  unit_price: string;
  quantity: string;
  unit: string;
  subtotal: string;
  expense_cycle: string;
}) => {
  const { application_date, expense_company, expense_purpose, warehouse, unit_price, quantity, unit, subtotal, expense_cycle } = data;

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    return isNaN(num) ? '0.00' : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  };

  return `以下費用請審批，請文字回復OK~thanks~

1）申請日期：${formatDate(application_date)}
2）費用所屬公司：${expense_company || ''}
3）費用用途：${expense_purpose || ''}
4）所屬貨倉：${warehouse || ''}
5）單價：$${formatCurrency(unit_price)}
6）數量：${quantity || ''}
7）單位：${unit || ''}
8）小計：$${formatCurrency(subtotal)}
9）費用週期：${expense_cycle || ''}`;
};

export default function ExpenseForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isEdit = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [calcError, setCalcError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    application_date: new Date().toISOString().split('T')[0],
    expense_company: '',
    expense_purpose: '',
    warehouse: '',
    unit_price: '',
    quantity: '',
    unit: '',
    subtotal: '',
    payment_method: '',
    expense_cycle: '',
    department: '',
    certificate_name: '',
    cycle_type: '',
    start_date: '',
    end_date: '',
    approval_text: '',
    approval_role: '',
    approver: '',
    status: '',
    remark: '',
    creator: 'SHELLEY.H',
  });

  const [approvalResult, setApprovalResult] = useState<{
    approval_role: string;
    approver: string;
    company_name?: string;
    stage?: string;
    level1_limit?: number;
    level2_limit?: number;
    needs_scott_approval?: boolean;
  } | null>(null);

  const calcRequestIdRef = useRef(0);

  useEffect(() => {
    if (isEdit && id) {
      fetchExpense(parseInt(id));
    } else if (!isEdit) {
      const params = new URLSearchParams(location.search);
      const source = params.get('source');
      if (source !== 'license') return;
      const prefill: Partial<typeof formData> = {};
      const v = (k: string) => params.get(k);
      if (v('application_date')) prefill.application_date = v('application_date')!;
      if (v('expense_company')) prefill.expense_company = v('expense_company')!;
      if (v('expense_purpose')) prefill.expense_purpose = v('expense_purpose')!;
      if (v('warehouse')) prefill.warehouse = v('warehouse')!;
      if (v('quantity')) prefill.quantity = v('quantity')!;
      if (v('unit')) prefill.unit = v('unit')!;
      if (v('expense_cycle')) prefill.expense_cycle = v('expense_cycle')!;
      if (v('department')) prefill.department = v('department')!;
      if (v('certificate_name')) prefill.certificate_name = v('certificate_name')!;
      if (v('cycle_type')) prefill.cycle_type = v('cycle_type')!;
      if (v('start_date')) prefill.start_date = v('start_date')!;
      if (v('end_date')) prefill.end_date = v('end_date')!;
      if (Object.keys(prefill).length > 0) {
        setFormData(prev => {
          const merged = { ...prev, ...prefill };
          const qty = parseFloat(merged.quantity || '0');
          const price = parseFloat(merged.unit_price || '0');
          if (qty > 0 && price > 0) {
            merged.subtotal = (qty * price).toFixed(2);
          } else if (qty > 0 && !price) {
            merged.subtotal = '';
          }
          merged.approval_text = generateApprovalTextHelper(merged);
          return merged;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, location.search]);

  const fetchExpense = async (expenseId: number) => {
    try {
      setIsLoading(true);
      const response = await expenseApi.getById(expenseId);
      const expense = response.data;
      setFormData({
        application_date: expense.application_date || new Date().toISOString().split('T')[0],
        expense_company: expense.expense_company || '',
        expense_purpose: expense.expense_purpose || '',
        warehouse: expense.warehouse || '',
        unit_price: expense.unit_price?.toString() || '',
        quantity: expense.quantity?.toString() || '',
        unit: expense.unit || '',
        subtotal: expense.subtotal?.toString() || '',
        payment_method: expense.payment_method || '',
        expense_cycle: expense.expense_cycle || '',
        department: expense.department || '',
        certificate_name: expense.certificate_name || '',
        cycle_type: expense.cycle_type || '',
        start_date: expense.start_date || '',
        end_date: expense.end_date || '',
        approval_text: expense.approval_text || '',
        approval_role: expense.approval_role || '',
        approver: expense.approver || '',
        status: (() => {
          const s = expense.status || '';
          if (s === 'HOLD' || s === '未交單 (HOLD)' || s === '0. 未交單 (HOLD)') return '0. HOLD';
          if (s === '1. 已交單, 待AP審批' || s === '已交單, 待AP審批') return '1. 已交單，待審批';
          return s;
        })(),
        remark: expense.remark || '',
        creator: expense.creator || 'SHELLEY.H',
      });
      setApprovalResult(null);
    } catch (error) {
      console.error('Failed to fetch expense:', error);
      alert(t('msg.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value,
      };
      
      const approvalText = generateApprovalTextHelper(newData);
      newData.approval_text = approvalText;
      
      return newData;
    });
  }, []);

  const calculateSubtotal = useCallback(() => {
    const unitPrice = parseFloat(formData.unit_price) || 0;
    const quantity = parseInt(formData.quantity) || 0;
    return (unitPrice * quantity).toFixed(2);
  }, [formData.unit_price, formData.quantity]);

  useEffect(() => {
    const expenseCompany = formData.expense_company?.trim();
    const unitPrice = parseFloat(formData.unit_price);
    const qty = parseInt(formData.quantity);
    const amount = unitPrice * qty;

    const hasExpenseCompany = !!expenseCompany;
    const hasValidAmount = !isNaN(amount) && amount > 0;

    if (!hasExpenseCompany || !hasValidAmount) {
      setApprovalResult(null);
      setIsCalculating(false);
      setCalcError(null);
      return;
    }

    setIsCalculating(true);
    setCalcError(null);

    const role = calcApprovalRole(expenseCompany, amount);
    const approver = calcApprover(role, expenseCompany);

    const result = {
      approval_role: role,
      approver: approver,
      company_name: expenseCompany,
      stage: role === 'SCOTT' ? 'scott' : role === '6M' ? 'level2' : 'level1',
      level1_limit: role === '5M' ? amount : 0,
      level2_limit: role === '6M' ? amount : 0,
      needs_scott_approval: role === 'SCOTT',
    };

    setApprovalResult(result);
    setFormData(prev => ({
      ...prev,
      approval_role: role || prev.approval_role,
      approver: approver || prev.approver,
    }));

    const autoApprovalText = generateApprovalTextHelper(formData);
    setFormData(prev => ({
      ...prev,
      approval_text: autoApprovalText,
    }));

    setIsCalculating(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.expense_company, formData.unit_price, formData.quantity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;

    if (!formData.expense_company) {
      alert(t('expense.errorCompany'));
      return;
    }

    if (!formData.unit_price || !formData.quantity) {
      alert(t('expense.errorAmount'));
      return;
    }

    try {
      setIsSaving(true);

      const expenseData = {
        ...formData,
        subtotal: parseFloat(calculateSubtotal()),
      };

      if (isEdit && id) {
        await expenseApi.update(parseInt(id), expenseData);
      } else {
        await expenseApi.create(expenseData);
      }

      alert(t('expense.saveSuccess'));
      const stateFrom = location.state?.from as string | undefined;
      if (stateFrom) {
        navigate(stateFrom);
        return;
      }
      const fromPage = sessionStorage.getItem('expenseList_fromPage');
      if (fromPage) {
        sessionStorage.removeItem('expenseList_fromPage');
        navigate(fromPage);
      } else {
        navigate(-1);
      }
    } catch (error: any) {
      const info = formatApiError(error, isEdit ? 'Update Expense' : 'Create Expense');
      alert(`${info.label}\n\n${info.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const goBack = useCallback(() => {
    if (isSaving) return;
    const stateFrom = location.state?.from as string | undefined;
    if (stateFrom) {
      navigate(stateFrom);
      return;
    }
    const fromPage = sessionStorage.getItem('expenseList_fromPage');
    if (fromPage) {
      sessionStorage.removeItem('expenseList_fromPage');
      navigate(fromPage);
    } else {
      navigate(-1);
    }
  }, [navigate, isSaving, location]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const subtotalValue = calculateSubtotal();
  const displayApprovalRole = approvalResult?.approval_role || formData.approval_role || '';
  const displayApprover = approvalResult?.approver || formData.approver || '';

  return (
    <div className="max-w-6xl mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={goBack}
            disabled={isSaving}
            className="flex items-center space-x-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('expense.returnToList')}</span>
          </button>
          <h1 className="text-2xl font-bold text-slate-800">
            {isEdit ? t('expense.editExpense') : t('expense.newExpense')}
          </h1>
        </div>
      </div>

      <form className="space-y-6">

        {/* ====================================================== */}
        {/* SECTION 1: USER INPUT (All Editable Fields)            */}
        {/* ====================================================== */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-slate-200 p-6 space-y-6">
          <div className="border-b border-slate-200 pb-4 flex items-center space-x-2">
            <User className="w-5 h-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-800">
              {t('expense.expenseInfo')}
            </h2>
          </div>

          {/* Row: Application Date, Expense Company, Expense Purpose */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('expense.applicationDate')} *
              </label>
              <input
                type="date"
                name="application_date"
                value={formData.application_date}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('expense.expenseCompany')} *
              </label>
              <input
                type="text"
                name="expense_company"
                value={formData.expense_company}
                onChange={handleChange}
                required
                placeholder={t('expense.companyCodePlaceholder')}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('expense.expensePurpose')} *
              </label>
              <input
                type="text"
                name="expense_purpose"
                value={formData.expense_purpose}
                onChange={handleChange}
                required
                placeholder={t('expense.expensePurpose')}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Row 1: Warehouse, Unit Price, Quantity, Unit */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('expense.warehouse')}</label>
              <input
                type="text"
                name="warehouse"
                value={formData.warehouse}
                onChange={handleChange}
                placeholder={t('expense.optional')}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('expense.unitPrice')} *
                <span className="ml-1 text-xs text-blue-500">{t('expense.triggersCalc')}</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="unit_price"
                value={formData.unit_price}
                onChange={handleChange}
                required
                placeholder="5000"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-semibold text-blue-700 bg-blue-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('expense.quantity')} *
                <span className="ml-1 text-xs text-blue-500">{t('expense.triggersCalc')}</span>
              </label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                required
                placeholder="1"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-semibold text-blue-700 bg-blue-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('expense.unit')}</label>
              <input
                type="text"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                placeholder={t('expense.unit')}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Row 2: Payment Method, Expense Cycle, Creator, (spacer) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('expense.paymentMethod')}</label>
              <select
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">{t('action.select')}</option>
                <option value="Check">Check</option>
                <option value="eCheck">eCheck</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Wire Transfer">Wire Transfer</option>
                <option value="ACH">ACH</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('expense.expenseCycle')}</label>
              <input
                type="text"
                name="expense_cycle"
                value={formData.expense_cycle}
                onChange={handleChange}
                placeholder={t('expense.cycleExample')}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('expense.creator')}</label>
              <select
                name="creator"
                value={formData.creator}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="SHELLEY.H">SHELLEY.H</option>
                <option value="CLAUDIA.Z">CLAUDIA.Z</option>
                <option value="Admin">Admin</option>
              </select>
            </div>

            <div className="hidden lg:block"></div>
          </div>

          {/* Row: Remark Textarea */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('expense.remark')}</label>
            <textarea
              name="remark"
              value={formData.remark}
              onChange={handleChange}
              rows={3}
              placeholder={t('expense.remarkPlaceholder')}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
            ></textarea>
          </div>
        </div>

        {/* ====================================================== */}
        {/* SECTION 2: SYSTEM CALCULATED (Read-Only)                */}
        {/* ====================================================== */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-amber-200 p-6 space-y-6">
          <div className="border-b border-amber-100 pb-4 flex items-center space-x-2">
            <Lock className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-slate-800">
              {t('expense.approvalInfoAuto')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Subtotal: Auto-calculated, read-only */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center space-x-1">
                <Lock className="w-3 h-3 text-amber-500" />
                <span>{t('expense.subtotalAuto')}</span>
              </label>
              <div className="w-full px-4 py-2.5 border-2 border-green-300 rounded-lg bg-green-50 text-green-700 font-bold text-lg text-center">
                ${subtotalValue}
              </div>
            </div>

            {/* Approval Role: System, read-only */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center space-x-1">
                <Lock className="w-3 h-3 text-amber-500" />
                <span>
                  {t('expense.approvalRole')}
                  {isCalculating && <span className="ml-2 text-xs text-blue-500">({t('expense.calculating')})</span>}
                </span>
              </label>
              <div className={`w-full px-4 py-2.5 rounded-lg text-sm font-semibold border-2 ${
                displayApprovalRole
                  ? 'bg-blue-50 text-blue-700 border-blue-300'
                  : 'bg-slate-50 text-slate-500 border-slate-200'
              }`}>
                {isCalculating ? (
                  <div className="flex items-center space-x-2 text-blue-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{t('expense.calculatingRole')}</span>
                  </div>
                ) : (
                  displayApprovalRole || t('expense.calcPlaceholder')
                )}
              </div>
            </div>

            {/* Approver: System, read-only */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center space-x-1">
                <Lock className="w-3 h-3 text-amber-500" />
                <span>
                  {t('expense.approver')}
                  {isCalculating && <span className="ml-2 text-xs text-blue-500">({t('expense.calculating')})</span>}
                </span>
              </label>
              <div className={`w-full px-4 py-2.5 rounded-lg text-sm font-semibold border-2 ${
                displayApprover
                  ? 'bg-blue-50 text-blue-700 border-blue-300'
                  : 'bg-slate-50 text-slate-500 border-slate-200'
              }`}>
                {isCalculating ? (
                  <div className="flex items-center space-x-2 text-blue-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{t('expense.calculatingApprover')}</span>
                  </div>
                ) : (
                  displayApprover || t('expense.calcPlaceholder')
                )}
              </div>
            </div>
          </div>

          {/* Approval Text: Auto-generated, read-only (NOT editable) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-slate-700 flex items-center space-x-1">
                <Lock className="w-3 h-3 text-amber-500" />
                <span>{t('expense.approvalText')}</span>
              </label>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(formData.approval_text);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                title="Copy to clipboard"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            <textarea
              name="approval_text"
              value={formData.approval_text}
              readOnly
              disabled
              rows={5}
              className="w-full px-4 py-3 border-2 border-amber-200 rounded-lg bg-amber-50 text-slate-800 font-mono text-sm resize-none cursor-not-allowed"
            ></textarea>
          </div>

          {calcError && (
            <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700 text-sm font-medium">{calcError}</span>
            </div>
          )}

          {approvalResult && (
            <div className="mt-4 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <h3 className="text-base font-bold text-blue-800">{t('expense.autoGenerated')}</h3>
                </div>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-white rounded-lg border-2 border-blue-100 shadow-sm">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <span className="text-blue-600 text-xs font-medium block mb-1">{t('expense.approvalRole')}</span>
                      <span className="text-blue-700 font-bold">{approvalResult.approval_role || '-'}</span>
                    </div>
                    <div className="p-2 bg-indigo-50 rounded-lg">
                      <span className="text-indigo-600 text-xs font-medium block mb-1">{t('expense.approver')}</span>
                      <span className="text-indigo-700 font-bold">{approvalResult.approver || '-'}</span>
                    </div>
                  </div>
                  {approvalResult.company_name && (
                    <div className="mt-3 pt-3 border-t border-blue-50 text-xs text-slate-500">
                      Resolved Company: {approvalResult.company_name}
                    </div>
                  )}
                </div>

                <div className="p-4 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl border-2 border-blue-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700 font-bold text-base block mb-2">{t('expense.completeApprovalRole')}</span>
                      <div className="text-blue-800 bg-white px-4 py-3 rounded-lg font-medium text-base shadow">
                        {approvalResult.approval_role}
                      </div>
                    </div>
                    <div>
                      <span className="text-blue-700 font-bold text-base block mb-2">{t('expense.allApprovers')}</span>
                      <div className="flex flex-wrap gap-2">
                        {approvalResult.approver ? (
                          <span className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-full shadow font-medium">
                            1. {approvalResult.approver}
                          </span>
                        ) : (
                          <span className="text-blue-600 bg-white px-4 py-2 rounded-lg shadow">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ====================================================== */}
        {/* SECTION 3: WORKFLOW (Controlled System Field)           */}
        {/* ====================================================== */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-purple-200 p-6 space-y-4">
          <div className="border-b border-purple-100 pb-4 flex items-center space-x-2">
            <Workflow className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-slate-800">
              {t('expense.status')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center space-x-1">
                <Workflow className="w-3 h-3 text-purple-500" />
                <span>{t('expense.status')}</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border-2 border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-purple-50 font-medium text-purple-800"
              >
                <option value="">{t('action.select')}</option>
                <option value="0. HOLD">0. HOLD</option>
                <option value="1. 已交單，待審批">1. 已交單，待審批</option>
                <option value="2. 已支付, 未交單 (無子公司)">2. 已支付, 未交單 (無子公司)</option>
                <option value="3. 待支付 (已交單並審批)">3. 待支付 (已交單並審批)</option>
                <option value="4. 已通知開支票">4. 已通知開支票</option>
                <option value="5. 已開支票並寄出">5. 已開支票並寄出</option>
                <option value="6. DONE">6. DONE</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row justify-end items-center space-y-3 sm:space-y-0 sm:space-x-4 pt-4">
          <button
            type="button"
            onClick={goBack}
            disabled={isSaving}
            className="w-full sm:w-auto px-8 py-3 text-sm font-medium border-2 border-slate-400 text-slate-700 rounded-lg hover:bg-slate-100 hover:border-slate-500 hover:shadow transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('action.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-10 py-3 text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{t('expense.saving')}</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>{t('action.save')}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
